/**
 * Die Linienzeichen der Oberfläche.
 *
 * Gezeichnet und nicht aus einer Bibliothek geholt. Drei Gründe, und der
 * dritte ist der wichtigste:
 *
 *   Eine Symbolbibliothek bringt tausend Zeichen mit, von denen wir zwölf
 *   brauchen. Das ist ein halbes Megabyte für nichts.
 *
 *   Sie ist eine weitere Abhängigkeit, die Versionen hat und irgendwann eine
 *   Umstellung verlangt – an einer Stelle, die reine Darstellung ist.
 *
 *   Und sie käme in ihrer eigenen Strichstärke daher. Hier haben alle Zeichen
 *   dieselbe: 1.7 Punkte auf einem Raster von 24, runde Enden, runde Ecken.
 *   Genau daran erkennt ein Auge, dass eine Oberfläche aus einem Guss ist –
 *   nicht am einzelnen Zeichen, sondern daran, dass alle gleich gezeichnet
 *   sind.
 *
 * Die Zeichen erben ihre Farbe vom Text darum (`currentColor`). Ein aktiver
 * Eintrag färbt damit sein Zeichen mit, ohne dass hier etwas davon weiss.
 */

export type ZeichenName =
  | 'uebersicht'
  | 'rechnung'
  | 'offerte'
  | 'rapport'
  | 'projekt'
  | 'zahlung'
  | 'kunde'
  | 'leistung'
  | 'einstellung'
  | 'suche'
  | 'glocke'
  | 'kalender'
  | 'haken'
  | 'paket'
  | 'mehr'

/** Die Pfade. Alle auf 24 × 24, alle ohne Füllung. */
const PFADE: Record<ZeichenName, JSX.Element> = {
  // Haus im Dokument – die Übersicht ist der Startpunkt.
  uebersicht: (
    <>
      <path d="M6 3.5h7.5L18 8v12.5H6z" />
      <path d="M13.5 3.5V8H18" />
      <path d="M9.6 15.4v-2.6l2.4-1.9 2.4 1.9v2.6z" />
    </>
  ),
  rechnung: (
    <>
      <path d="M6 3.5h7.5L18 8v12.5H6z" />
      <path d="M13.5 3.5V8H18" />
      <path d="M9 12h6M9 15.2h6M9 18h3.5" />
    </>
  ),
  offerte: (
    <>
      <path d="M6 3.5h7.5L18 8v12.5H6z" />
      <path d="M13.5 3.5V8H18" />
      <path d="M9 13h6M9 16.4h4" />
    </>
  ),
  rapport: (
    <>
      <path d="M6 3.5h7.5L18 8v12.5H6z" />
      <path d="M13.5 3.5V8H18" />
      <path d="M9 12.4h1.6M12.4 12.4H15M9 15.6h1.6M12.4 15.6H15M9 18.4h1.6" />
    </>
  ),
  projekt: (
    <>
      <path d="M4 7.2a1.7 1.7 0 0 1 1.7-1.7h3.1l1.8 2.2h6.7A1.7 1.7 0 0 1 19 9.4v7.9a1.7 1.7 0 0 1-1.7 1.7H5.7A1.7 1.7 0 0 1 4 17.3z" />
    </>
  ),
  zahlung: (
    <>
      <rect x="3.6" y="6" width="16.8" height="12" rx="2.2" />
      <path d="M3.6 10.2h16.8" />
      <path d="M7.2 14.4h3.4" />
    </>
  ),
  kunde: (
    <>
      <circle cx="9.4" cy="8.6" r="3.1" />
      <path d="M4.2 19.4c0-2.9 2.3-5 5.2-5s5.2 2.1 5.2 5" />
      <path d="M15.6 6.2a3 3 0 0 1 0 5.6M17.2 19.4c0-1.9-.5-3.3-1.3-4.3" />
    </>
  ),
  leistung: (
    <>
      <rect x="3.8" y="8.2" width="16.4" height="10.6" rx="2" />
      <path d="M9.2 8.2V6.6A1.6 1.6 0 0 1 10.8 5h2.4a1.6 1.6 0 0 1 1.6 1.6v1.6" />
      <path d="M12 12v2.6" />
    </>
  ),
  einstellung: (
    <>
      <circle cx="12" cy="12" r="2.9" />
      <path d="M12 3.6v2.1M12 18.3v2.1M20.4 12h-2.1M5.7 12H3.6M17.9 6.1l-1.5 1.5M7.6 16.4l-1.5 1.5M17.9 17.9l-1.5-1.5M7.6 7.6 6.1 6.1" />
    </>
  ),
  suche: (
    <>
      <circle cx="11" cy="11" r="6.4" />
      <path d="m16 16 4.2 4.2" />
    </>
  ),
  glocke: (
    <>
      <path d="M12 4.2a5.6 5.6 0 0 0-5.6 5.6c0 4.4-1.6 5.8-1.6 5.8h14.4s-1.6-1.4-1.6-5.8A5.6 5.6 0 0 0 12 4.2Z" />
      <path d="M10.4 18.6a1.9 1.9 0 0 0 3.2 0" />
    </>
  ),
  kalender: (
    <>
      <rect x="4" y="5.6" width="16" height="14.4" rx="2" />
      <path d="M4 10h16M8.6 3.6v3.6M15.4 3.6v3.6" />
    </>
  ),
  haken: (
    <>
      <rect x="4" y="4" width="16" height="16" rx="3.2" />
      <path d="m8.4 12.2 2.6 2.6 4.8-5" />
    </>
  ),
  // Ein Karton von der Ecke gesehen – das gebuchte Paket, nicht ein Beleg.
  paket: (
    <>
      <path d="M12 3.6 20.4 8v8L12 20.4 3.6 16V8Z" />
      <path d="M3.6 8 12 12.4 20.4 8" />
      <path d="M12 12.4v8" />
    </>
  ),
  mehr: (
    <>
      <circle cx="5.4" cy="12" r="1.4" />
      <circle cx="12" cy="12" r="1.4" />
      <circle cx="18.6" cy="12" r="1.4" />
    </>
  )
}

/*
  «mehr» sind drei gefüllte Punkte – als einziges Zeichen ohne Strich. Deshalb
  bekommt es hier eine eigene Behandlung statt einer Ausnahme im Aufrufer.
*/
const GEFUELLT: ZeichenName[] = ['mehr']

export function Zeichen({
  name,
  groesse = 20
}: {
  name: ZeichenName
  groesse?: number
}): JSX.Element {
  const gefuellt = GEFUELLT.includes(name)
  return (
    <svg
      className="rp-zeichen"
      width={groesse}
      height={groesse}
      viewBox="0 0 24 24"
      aria-hidden="true"
      fill={gefuellt ? 'currentColor' : 'none'}
      stroke={gefuellt ? 'none' : 'currentColor'}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {PFADE[name]}
    </svg>
  )
}
